import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import api from "../../../api/axios";
import { useScanHandler } from "../../../context/ScannerContext";
import "../../../styles/warehouse/receipt-modal.css";

interface InvRow { id: number; date: string; title: string | null; comment: string | null; posted: boolean; createdBy: string; itemsCount: number; countedCount: number; diffTotal: string; diffCount: number; postedAt: string | null }
interface InvItem { productId: number; nameRu: string; sku: string | null; barcode: string | null; expectedQty: string; countedQty: string | null; currentQty: string }

const num = (v: any) => Number(v ?? 0);
const fmt = (v: number) => v.toLocaleString("ru-RU", { maximumFractionDigits: 3 });

export default function InventoryTab() {
  const [rows, setRows] = useState<InvRow[]>([]);
  const [openId, setOpenId] = useState<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await api.get("/warehouse/inventory");
    setRows(r.data ?? []);
  }
  useEffect(() => { load().catch(() => undefined); }, []);

  async function create() {
    const title = window.prompt("Название инвентаризации (необязательно)", `Инвентаризация ${new Date().toLocaleDateString("ru-RU")}`);
    if (title === null) return;
    setBusy(true);
    try {
      const r = await api.post("/warehouse/inventory", { title });
      await load();
      setOpenId(r.data.id);
    } catch (e: any) {
      alert(e?.response?.data?.message || "Не удалось создать инвентаризацию");
    } finally { setBusy(false); }
  }

  return (
    <div className="warehouse-issues-tab">
      <div className="issues-header">
        <div className="issues-header-info">
          <h2 className="issues-title">Инвентаризация</h2>
          <div className="issues-stats">
            <span>{rows.length} документов</span>
            <span className="issues-stats-dot">·</span>
            <span>{rows.filter((r) => r.posted).length} проведено</span>
          </div>
        </div>
        <button className="issues-create-btn" onClick={create} disabled={busy}>+ Новая инвентаризация</button>
      </div>

      <div style={{ padding: "0 20px 20px", overflowX: "auto" }}>
        <table className="stock-table">
          <thead>
            <tr><th>№</th><th>Дата</th><th>Название</th><th>Посчитано</th><th>Расхождения</th><th>Статус</th><th>Кто</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>Инвентаризаций пока нет. Нажмите «Новая инвентаризация»: остатки зафиксируются, а факт вносится сканером или вручную.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(r.id)}>
                <td style={{ fontWeight: 600, color: "#818cf8" }}>#{r.id}</td>
                <td>{new Date(r.date).toLocaleDateString("ru-RU")}</td>
                <td>{r.title || "—"}</td>
                <td>{r.countedCount} из {r.itemsCount}</td>
                <td>{r.diffCount ? <span style={{ color: "#fbbf24" }}>{r.diffCount} поз., {num(r.diffTotal) > 0 ? "+" : ""}{fmt(num(r.diffTotal))} шт.</span> : <span style={{ color: "#6b7280" }}>нет</span>}</td>
                <td>{r.posted ? <span className="tag-in">Проведена</span> : <span className="tag-out">Черновик</span>}</td>
                <td style={{ color: "#9ca3af", fontSize: 12 }}>{r.createdBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId !== undefined && <InventoryModal id={openId} onClose={() => setOpenId(undefined)} onChanged={load} />}
    </div>
  );
}

function InventoryModal({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [posted, setPosted] = useState(false);
  const [items, setItems] = useState<InvItem[]>([]);
  const [filter, setFilter] = useState<"all" | "counted" | "uncounted" | "diff">("all");
  const [term, setTerm] = useState("");
  const [zero, setZero] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const inRef = useRef<HTMLInputElement>(null);

  function apply(d: any) {
    setTitle(d.title ?? ""); setComment(d.comment ?? ""); setPosted(!!d.posted); setItems(d.items ?? []); setDirty(false);
  }
  useEffect(() => { api.get(`/warehouse/inventory/${id}`).then((r) => apply(r.data)).catch(() => undefined); }, [id]);
  useEffect(() => { if (!flash) return; const t = setTimeout(() => setFlash(null), 3000); return () => clearTimeout(t); }, [flash]);

  const setCounted = (pid: number, v: string | null) => {
    setItems((prev) => prev.map((x) => (x.productId === pid ? { ...x, countedQty: v } : x)));
    setDirty(true);
  };

  // скан: +1 к фактическому количеству товара
  function scan(raw: string) {
    const t = raw.trim().toLowerCase();
    if (!t || posted) return false;
    const it = items.find((x) => (x.barcode ?? "").toLowerCase() === t || (x.sku ?? "").toLowerCase() === t);
    if (!it) { setFlash({ ok: false, text: `«${raw.trim()}» нет в этой инвентаризации` }); return false; }
    const next = num(it.countedQty) + 1;
    setCounted(it.productId, String(next));
    setFlash({ ok: true, text: `${it.nameRu}: факт ${next}` });
    return true;
  }
  useScanHandler((code) => { scan(code); });

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase();
    const parts = t.split(/\s+/).filter(Boolean);
    return items.filter((x) => {
      if (filter === "counted" && x.countedQty === null) return false;
      if (filter === "uncounted" && x.countedQty !== null) return false;
      if (filter === "diff" && (x.countedQty === null || num(x.countedQty) === num(x.expectedQty))) return false;
      if (!parts.length) return true;
      const h = `${x.nameRu} ${x.sku ?? ""} ${x.barcode ?? ""}`.toLowerCase();
      return parts.every((p) => h.includes(p));
    });
  }, [items, filter, term]);

  const counted = items.filter((x) => x.countedQty !== null);
  const diffItems = counted.filter((x) => num(x.countedQty) !== num(x.expectedQty));
  const surplus = diffItems.reduce((s, x) => s + Math.max(0, num(x.countedQty) - num(x.expectedQty)), 0);
  const shortage = diffItems.reduce((s, x) => s + Math.max(0, num(x.expectedQty) - num(x.countedQty)), 0);

  async function save(): Promise<boolean> {
    setBusy(true);
    try {
      const r = await api.put(`/warehouse/inventory/${id}`, {
        title, comment,
        items: items.map((x) => ({ productId: x.productId, countedQty: x.countedQty === null || x.countedQty === "" ? null : Number(x.countedQty) })),
      });
      apply(r.data); onChanged();
      return true;
    } catch (e: any) {
      const m = e?.response?.data?.message; alert(Array.isArray(m) ? m.join("\n") : m || "Не удалось сохранить");
      return false;
    } finally { setBusy(false); }
  }

  async function post() {
    const uncounted = items.length - counted.length;
    const msg = `Провести инвентаризацию?\nПосчитано ${counted.length} из ${items.length}. Излишки: ${fmt(surplus)} шт., недостача: ${fmt(shortage)} шт.\n` +
      (uncounted ? (zero ? `Непосчитанные (${uncounted}) будут обнулены.` : `Непосчитанные (${uncounted}) останутся как есть.`) : "") +
      `\nОстатки на складе станут равны факту.`;
    if (!window.confirm(msg)) return;
    if (!(await save())) return;
    setBusy(true);
    try {
      const r = await api.post(`/warehouse/inventory/${id}/post`, { uncountedAsZero: zero });
      apply(r.data); onChanged();
    } catch (e: any) {
      const m = e?.response?.data?.message; alert(Array.isArray(m) ? m.join("\n") : m || "Не удалось провести");
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Удалить черновик инвентаризации?")) return;
    try { await api.delete(`/warehouse/inventory/${id}`); onChanged(); onClose(); }
    catch (e: any) { alert(e?.response?.data?.message || "Не удалось удалить"); }
  }

  function close() {
    if (dirty && !window.confirm("Есть несохранённый пересчёт. Закрыть без сохранения?")) return;
    onClose();
  }

  const chip = (k: typeof filter, label: string) => (
    <button className={k === filter ? "primary-btn" : "secondary-btn"} style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setFilter(k)}>{label}</button>
  );

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-window" style={{ maxWidth: 1040, width: "97vw", maxHeight: "94vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-header">
          <div className="modal-title">Инвентаризация №{id} {posted && <span className="issue-badge-posted">Проведена</span>}</div>
          <button className="modal-close" onClick={close}>×</button>
        </div>

        <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="primary-input" style={{ flex: "1 1 220px" }} placeholder="Название" value={title} disabled={posted} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} />
            <input className="primary-input" style={{ flex: "2 1 260px" }} placeholder="Комментарий" value={comment} disabled={posted} onChange={(e) => { setComment(e.target.value); setDirty(true); }} />
          </div>

          <div className="rc-add" style={{ marginTop: 10 }}>
            <input
              ref={inRef}
              className="input"
              placeholder={posted ? "Поиск по названию, артикулу или ШК" : "Сканируйте ШК (+1 к факту) или ищите по названию"}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (scan(term)) setTerm(""); } }}
              autoComplete="off"
            />
          </div>
          {flash && <div className={`rc-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0", alignItems: "center" }}>
            {chip("all", `Все (${items.length})`)}
            {chip("counted", `Посчитано (${counted.length})`)}
            {chip("uncounted", `Не посчитано (${items.length - counted.length})`)}
            {chip("diff", `Расхождения (${diffItems.length})`)}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>Излишки: <b style={{ color: "#34d399" }}>+{fmt(surplus)}</b> · Недостача: <b style={{ color: "#f87171" }}>−{fmt(shortage)}</b></span>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="rc-table">
              <thead>
                <tr><th>Товар</th><th style={{ width: 130 }}>ШК</th><th style={{ width: 80 }}>Учёт</th><th style={{ width: 100 }}>Факт</th><th style={{ width: 80 }}>Разница</th></tr>
              </thead>
              <tbody>
                {shown.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: 18, color: "#6b7280" }}>Ничего не найдено</td></tr>}
                {shown.map((x) => {
                  const diff = x.countedQty === null ? null : num(x.countedQty) - num(x.expectedQty);
                  return (
                    <tr key={x.productId}>
                      <td className="rc-name">{x.nameRu}<small>{x.sku}</small></td>
                      <td style={{ fontSize: 12, color: "#9ca3af" }}>{x.barcode || "—"}</td>
                      <td>{fmt(num(x.expectedQty))}{num(x.currentQty) !== num(x.expectedQty) && <small style={{ display: "block", color: "#fbbf24" }}>сейчас {fmt(num(x.currentQty))}</small>}</td>
                      <td className="num">
                        <input type="number" min={0} disabled={posted} value={x.countedQty ?? ""} placeholder="—" onFocus={(e) => e.target.select()}
                          onChange={(e) => setCounted(x.productId, e.target.value === "" ? null : String(Math.max(0, +e.target.value)))} />
                      </td>
                      <td style={{ fontWeight: 600, color: diff === null ? "#6b7280" : diff === 0 ? "#6b7280" : diff > 0 ? "#34d399" : "#f87171" }}>{diff === null ? "" : diff > 0 ? `+${fmt(diff)}` : fmt(diff)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer" style={{ flexWrap: "wrap" }}>
          {posted ? (
            <button className="secondary-btn" onClick={onClose}>Закрыть</button>
          ) : (
            <>
              <button className="secondary-btn danger-btn" onClick={remove} disabled={busy} style={{ marginRight: "auto" }}>Удалить</button>
              <label style={{ fontSize: 12, color: "#9ca3af", display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={zero} onChange={(e) => setZero(e.target.checked)} /> Непосчитанные считать нулём
              </label>
              <button className="secondary-btn" onClick={save} disabled={busy}>Сохранить</button>
              <button className="primary-btn" onClick={post} disabled={busy}>Провести</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
